/**
 * Utility functions for formatting dates consistently across the application
 * Handles Firestore Timestamps, JavaScript Dates, and various date formats
 */

/**
 * Format a date value to a localized string with date and time
 * @param {*} date - Date value (Firestore Timestamp, Date object, number, or string)
 * @param {Object} options - Optional formatting options
 * @returns {string} - Formatted date string or 'N/A' if invalid
 */
export const formatDateTime = (date, options = {}) => {
  if (!date) return 'N/A';

  const defaultOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...options
  };

  try {
    // Handle Firestore Timestamp with toDate() method
    if (date.toDate && typeof date.toDate === 'function') {
      const jsDate = date.toDate();
      if (!isNaN(jsDate.getTime())) {
        return jsDate.toLocaleString('en-ZA', defaultOptions);
      }
    }

    // Handle JavaScript Date object
    if (date instanceof Date) {
      if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-ZA', defaultOptions);
      }
    }

    // Handle timestamp number (milliseconds)
    if (typeof date === 'number') {
      const jsDate = new Date(date);
      if (!isNaN(jsDate.getTime())) {
        return jsDate.toLocaleString('en-ZA', defaultOptions);
      }
    }

    // Handle Firestore Timestamp object with seconds/nanoseconds
    if (date.seconds !== undefined || date._seconds !== undefined) {
      const seconds = date.seconds || date._seconds;
      const jsDate = new Date(seconds * 1000);
      if (!isNaN(jsDate.getTime())) {
        return jsDate.toLocaleString('en-ZA', defaultOptions);
      }
    }

    // Try parsing as string
    const parsedDate = new Date(date);
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate.toLocaleString('en-ZA', defaultOptions);
    }

    return 'N/A';
  } catch (error) {
    console.warn('Error formatting date:', date, error);
    return 'N/A';
  }
};

/**
 * Format a date value to a localized date string (no time)
 * @param {*} date - Date value
 * @returns {string} - Formatted date string or 'N/A' if invalid
 */
export const formatDate = (date) => {
  return formatDateTime(date, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: undefined,
    minute: undefined
  });
};

/**
 * Format a date value to a localized time string (no date)
 * @param {*} date - Date value
 * @returns {string} - Formatted time string or 'N/A' if invalid
 */
export const formatTime = (date) => {
  return formatDateTime(date, {
    year: undefined,
    month: undefined,
    day: undefined,
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Convert various date formats to a JavaScript Date object
 * @param {*} date - Date value
 * @returns {Date|null} - JavaScript Date object or null if invalid
 */
export const toJSDate = (date) => {
  if (!date) return null;

  try {
    // Handle Firestore Timestamp
    if (date.toDate && typeof date.toDate === 'function') {
      return date.toDate();
    }

    // Already a Date object
    if (date instanceof Date) {
      return isNaN(date.getTime()) ? null : date;
    }

    // Handle timestamp number
    if (typeof date === 'number') {
      const jsDate = new Date(date);
      return isNaN(jsDate.getTime()) ? null : jsDate;
    }

    // Handle Firestore Timestamp object
    if (date.seconds !== undefined || date._seconds !== undefined) {
      const seconds = date.seconds || date._seconds;
      return new Date(seconds * 1000);
    }

    // Try parsing as string
    const parsedDate = new Date(date);
    return isNaN(parsedDate.getTime()) ? null : parsedDate;
  } catch (error) {
    console.warn('Error converting to JS Date:', date, error);
    return null;
  }
};

/**
 * Check if a date value is valid
 * @param {*} date - Date value
 * @returns {boolean} - True if valid date
 */
export const isValidDate = (date) => {
  return toJSDate(date) !== null;
};

const dateFormatter = {
  formatDateTime,
  formatDate,
  formatTime,
  toJSDate,
  isValidDate
};
export default dateFormatter;
